import { replaceClinicInfoPlaceholders } from "../config/clinicPlaceholders";
import { ThemeSettings } from "../types";

interface OnboardingData {
  clinicInfo: {
    clinicName: string;
    phone: string;
    email: string;
    address: string;
    lineId: string;
    logoUrl: string;
  };
  theme: {
    primaryColor: string;
    secondaryColor: string;
    template: "clean" | "premium";
    selectedTheme?: "beauty-clinic" | "medical-center" | "modern-clinic";
  };
  pages: {
    home: boolean;
    services: boolean;
    doctors: boolean;
    about: boolean;
    contact: boolean;
    blog: boolean;
  };
}

function createEmptyPageContent(): string {
  return JSON.stringify({
    ROOT: {
      type: { resolvedName: "RootContainer" },
      props: {
        backgroundColor: "#ffffff",
        padding: 0,
      },
      nodes: [],
    },
  });
}

const PAGE_HREF_MAP: Record<string, string[]> = {
  home: ["/", "/home"],
  services: ["/services"],
  doctors: ["/doctors"],
  about: ["/about"],
  contact: ["/contact"],
  blog: ["/blog"],
};

function filterNavigationBySelectedPages(content: string, selectedPages: OnboardingData["pages"]): string {
  try {
    const parsed = JSON.parse(content);

    const allowedHrefs = new Set<string>();
    for (const [pageKey, isSelected] of Object.entries(selectedPages)) {
      if (isSelected && PAGE_HREF_MAP[pageKey]) {
        PAGE_HREF_MAP[pageKey].forEach((href) => allowedHrefs.add(href));
      }
    }

    for (const nodeId of Object.keys(parsed)) {
      const node = parsed[nodeId];
      if (!node?.type?.resolvedName) continue;

      if (node.type.resolvedName === "Navbar" && node.props?.navItems) {
        node.props.navItems = node.props.navItems.filter((item: { href: string }) => {
          if (!item.href) return true;
          if (item.href.startsWith("http") || item.href.startsWith("#")) return true;
          if (item.href.startsWith("tel:") || item.href.startsWith("mailto:")) return true;
          return allowedHrefs.has(item.href);
        });
      }

      if (node.type.resolvedName === "Footer" && node.props?.columns) {
        node.props.columns = node.props.columns.map(
          (column: { title: string; links: Array<{ label: string; href: string }> }) => ({
            ...column,
            links: column.links.filter((link: { href: string }) => {
              if (!link.href) return true;
              if (link.href.startsWith("http") || link.href.startsWith("#")) return true;
              if (link.href.startsWith("tel:") || link.href.startsWith("mailto:")) return true;
              if (link.href.split("/").length > 2) return true;
              return allowedHrefs.has(link.href);
            }),
          })
        );
        node.props.columns = node.props.columns.filter((column: { links: unknown[] }) => column.links.length > 0);
      }
    }

    return JSON.stringify(parsed);
  } catch (err) {
    console.warn("Could not filter navigation:", err);
    return content;
  }
}

export const generateWebsite = async (
  siteId: string,
  data: OnboardingData
): Promise<{ success: boolean; pages?: Array<{ id: string; slug: string }>; error?: string }> => {
  try {
    const { createPage, getAllTemplates } = await import("./firestore");
    const createdPages: Array<{ id: string; slug: string }> = [];

    const pageTheme: ThemeSettings = {
      primary: data.theme.primaryColor,
      secondary: data.theme.secondaryColor,
      font: "Sarabun",
    };

    let templates: Array<{ id: string; name: string; category: string; theme?: string; content: string }> = [];
    try {
      templates = await getAllTemplates();
    } catch (err) {
      console.warn("Could not fetch templates:", err);
    }

    const selectedTheme = data.theme.selectedTheme;

    const pageConfigs = [
      { key: "home", name: "Home", slug: "home", templateCategory: "home" },
      { key: "services", name: "Services", slug: "services", templateCategory: "services" },
      { key: "doctors", name: "Doctors", slug: "doctors", templateCategory: "doctors" },
      { key: "about", name: "About", slug: "about", templateCategory: "about" },
      { key: "contact", name: "Contact", slug: "contact", templateCategory: "contact" },
      { key: "blog", name: "Blog", slug: "blog", templateCategory: "blog" },
    ];

    for (const config of pageConfigs) {
      if (data.pages[config.key as keyof typeof data.pages]) {
        let template;

        if (selectedTheme) {
          template = templates.find((t) => t.theme === selectedTheme && t.category === config.templateCategory);
        }

        if (!template) {
          template = templates.find((t) => t.category === config.templateCategory && !t.theme);
        }

        if (!template) {
          template = templates.find((t) => t.category === config.templateCategory);
        }

        let content: string;
        if (template && template.content) {
          content = replaceClinicInfoPlaceholders(template.content, data.clinicInfo);
          content = filterNavigationBySelectedPages(content, data.pages);
        } else {
          console.warn(`No template found for ${config.templateCategory}, creating empty page`);
          content = createEmptyPageContent();
        }

        const page = await createPage(siteId, config.name, config.slug, content, pageTheme);
        createdPages.push({ id: page.id, slug: page.slug });
      }
    }

    return { success: true, pages: createdPages };
  } catch (error) {
    console.error("Error generating website:", error);
    return { success: false, error: error instanceof Error ? error.message : "Failed to generate website" };
  }
};
